import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import React, { useState } from "react";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import { showToast } from "src/utils/toaster/showToast";
import { STAFF_FILE_REMARK_MAX_LENGTH } from "./staffFileRules";

type Props = {
  /** Existing note, so editing starts from what is already there. */
  initialRemark: string;
  onClose: () => void;
  /** Resolves to an error message, or null on success. */
  onSave: (remark: string) => Promise<string | null>;
  isSaving: boolean;
};

const TempRemarkDialog = ({
  initialRemark,
  onClose,
  onSave,
  isSaving,
}: Props) => {
  const [remark, setRemark] = useState(initialRemark || "");
  const [error, setError] = useState("");

  const handleSave = async () => {
    const text = remark.trim();
    if (!text) {
      // Clearing has its own button, so an empty save here is a slip rather
      // than an intention.
      showToast("error", "Write the remark, or use Clear to remove it.");
      return;
    }
    const err = await onSave(text);
    if (err) {
      setError(err);
      return;
    }
    onClose();
  };

  return (
    <Dialog open={true} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div className="flex justify-start text-primary-main font-bold">
          {initialRemark ? "Edit Temporary Remark" : "Add Temporary Remark"}
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-3 pt-1">
          <div className="text-[12px] text-slate-500">
            Say which document is still needed. The next assignee sees this on
            the application; whoever attaches the file clears it.
          </div>

          <div>
            <textarea
              value={remark}
              disabled={isSaving}
              maxLength={STAFF_FILE_REMARK_MAX_LENGTH}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="e.g. Address proof missing — please attach before processing"
              rows={4}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
            />
            {/* The textarea stops accepting input silently at the cap. */}
            <div className="text-right text-[11px] text-slate-500">
              {remark.length}/{STAFF_FILE_REMARK_MAX_LENGTH}
            </div>
          </div>

          {error && <div className="text-[12px] text-red-500">{error}</div>}

          <div className="flex justify-end gap-3 pb-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm text-slate-600 disabled:opacity-40"
            >
              Cancel
            </button>
            <ATMLoadingButton
              isLoading={isSaving}
              onClick={handleSave}
              className="px-6"
            >
              Save Remark
            </ATMLoadingButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TempRemarkDialog;
